import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ExamRecord } from '@/types';
import Field, { inputClass } from '@/components/base/Field';

interface ExamRecordSectionProps {
  studentId: string;
}

interface Draft {
  schoolName: string;
  year: string;
  grade: string;
  semester: string;
  examType: string;
  score: string;
  expectedGrade: string;
}

const emptyDraft: Draft = {
  schoolName: '',
  year: String(new Date().getFullYear()),
  grade: '고1',
  semester: '1학기',
  examType: '중간고사',
  score: '',
  expectedGrade: '',
};

const GRADES = ['중1', '중2', '중3', '고1', '고2', '고3'];

export default function ExamRecordSection({ studentId }: ExamRecordSectionProps) {
  const [records, setRecords] = useState<ExamRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('exam_records')
        .select('*')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setRecords((data as ExamRecord[]) ?? []);
    } catch {
      setError('학교기출 기록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.year.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { data, error: insertError } = await supabase
        .from('exam_records')
        .insert({
          student_id: studentId,
          school_name: draft.schoolName.trim(),
          year: draft.year.trim(),
          grade: draft.grade,
          semester: draft.semester,
          exam_type: draft.examType,
          score: draft.score.trim(),
          expected_grade: draft.expectedGrade.trim(),
        })
        .select();
      if (insertError) throw insertError;
      const created = (data as ExamRecord[] | null)?.[0];
      if (created) setRecords((prev) => [created, ...prev]);
      setDraft(emptyDraft);
      setOpen(false);
    } catch {
      setError('학교기출 기록 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      const { error: deleteError } = await supabase.from('exam_records').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setRecords((prev) => prev.filter((r) => r.id !== id));
    } catch {
      setError('삭제에 실패했어요.');
    }
  };

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-file-list-3-line"></i>
            </span>
            학교기출 결과
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            학교 이름, 연도·학년·학기, 시험 종류, 점수, 예상 등급을 기록해요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
        >
          <i className={open ? 'ri-close-line' : 'ri-add-line'}></i>
          {open ? '닫기' : '결과 추가'}
        </button>
      </div>

      {open && (
        <form
          onSubmit={handleAdd}
          className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-2"
        >
          <Field label="학교 이름">
            <input
              type="text"
              value={draft.schoolName}
              onChange={(e) => set('schoolName', e.target.value)}
              placeholder="예: 대치중학교"
              className={inputClass}
            />
          </Field>
          <Field label="연도" required>
            <input
              type="text"
              value={draft.year}
              onChange={(e) => set('year', e.target.value)}
              placeholder="예: 2026"
              className={inputClass}
            />
          </Field>
          <Field label="학년">
            <select value={draft.grade} onChange={(e) => set('grade', e.target.value)} className={inputClass}>
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <Field label="학기">
            <select
              value={draft.semester}
              onChange={(e) => set('semester', e.target.value)}
              className={inputClass}
            >
              <option value="1학기">1학기</option>
              <option value="2학기">2학기</option>
            </select>
          </Field>
          <Field label="시험 종류">
            <select
              value={draft.examType}
              onChange={(e) => set('examType', e.target.value)}
              className={inputClass}
            >
              <option value="중간고사">중간고사</option>
              <option value="기말고사">기말고사</option>
            </select>
          </Field>
          <Field label="점수">
            <input
              type="text"
              value={draft.score}
              onChange={(e) => set('score', e.target.value)}
              placeholder="예: 92 / 100"
              className={inputClass}
            />
          </Field>
          <Field label="예상 등급">
            <input
              type="text"
              value={draft.expectedGrade}
              onChange={(e) => set('expectedGrade', e.target.value)}
              placeholder="예: 2등급"
              className={inputClass}
            />
          </Field>
          <div className="flex items-end sm:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              기록 저장
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : records.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
          아직 기록된 학교기출 결과가 없어요.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {records.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-950">
                  {r.year}년 {r.grade} {r.semester} {r.exam_type}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-building-line"></i>
                    {r.school_name || '학교 미입력'}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                  점수 {r.score || '-'}
                </span>
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                  {r.expected_grade || '등급 미입력'}
                </span>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  aria-label="삭제"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}